import React, { useContext } from 'react';
import { Ticket } from '@certhon/models';

// Global tickets store
export interface TicketStore {
  getTicket: (key: string) => Ticket | undefined;
  getAllTickets: () => Ticket[];
  isLoading: boolean;
}

export const TicketsStore = React.createContext<TicketStore | null>(null);

export const useTicketsStore = () => {
  const context = useContext(TicketsStore);
  if (!context) {
    throw new Error(
      'useTicketsStore must be used within a TicketsStoreProvider',
    );
  }
  return context;
};

// Hook to create a ticket store
export function useCreateTicketStore(
  tickets?: Ticket[],
  isLoading: boolean = false,
): TicketStore {
  return React.useMemo((): TicketStore => {
    const byId = new Map<string, Ticket>();
    const lookupCache = new Map<string, string>(); // lookup key -> ticket id

    if (tickets) {
      tickets.forEach((ticket) => {
        // Store each ticket only once by ID to avoid memory duplication
        byId.set(ticket.id, ticket);

        // Create lookup mappings to the ticket ID
        lookupCache.set(ticket.id, ticket.id);
        if (ticket.number) {
          lookupCache.set(ticket.number.toString(), ticket.id);
        }
      });
    }

    return {
      getTicket: (key: string) => {
        const ticketId = lookupCache.get(key);
        return ticketId ? byId.get(ticketId) : undefined;
      },
      getAllTickets: () => Array.from(byId.values()),
      isLoading: isLoading,
    };
  }, [tickets, isLoading]);
}
